import React, { useState, useEffect } from 'react';
import './CropDiseaseDetection.css';
import Footer from './Footer';
import ContactCropDisease from './ContactCropDisease';
import SEO from './SEO';

const CropDiseaseDetection = () => {
  const [phoneStep, setPhoneStep] = useState(1); // 1: Upload, 2: Analyze, 3: Report
  const [ndviStep, setNdviStep] = useState(1); // 1: Select Land, 2: Fetch Satellite, 3: Process, 4: Report

  // Auto-cycle through phone steps
  useEffect(() => {
    const timer = setInterval(() => {
      setPhoneStep(prev => prev >= 3 ? 1 : prev + 1);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Auto-cycle through NDVI steps
  useEffect(() => {
    const timer = setInterval(() => {
      setNdviStep(prev => prev >= 4 ? 1 : prev + 1);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const capabilities = [
    {
      icon: '🔬',
      title: 'Disease & Pest Detection',
      description: 'Image-based detection of crop diseases and pests with high accuracy'
    },
    {
      icon: '🧪',
      title: 'Nutrient Analysis',
      description: 'Identification of nutrient deficiencies and stress symptoms'
    },
    {
      icon: '💊',
      title: 'Treatment Guidance',
      description: 'Treatment recommendations and preventive measures'
    },
    {
      icon: '📊',
      title: 'Severity Assessment',
      description: 'Severity assessment and detailed symptom explanations'
    },
    {
      icon: '🌾',
      title: 'Multi-Crop Support',
      description: 'Support for multiple major crops and hundreds of disease classes'
    },
    {
      icon: '🤖',
      title: 'Continuous Learning',
      description: 'Continuous improvement through machine learning models'
    }
  ];

  const benefits = [
    { icon: '⚡', text: 'Faster diagnosis compared to manual inspection' },
    { icon: '🛡️', text: 'Reduced crop losses through early detection' },
    { icon: '👨‍🌾', text: 'Lower dependence on scarce agronomy experts' },
    { icon: '📈', text: 'Data-driven decisions for crop protection' },
    { icon: '🏭', text: 'Scalable solution for farms of any size' }
  ];

  const steps = [
    { number: '01', title: 'Capture', description: 'Take a photo of the affected plant or leaf' },
    { number: '02', title: 'Upload', description: 'Upload the image to the system' },
    { number: '03', title: 'Analyze', description: 'AI model analyzes visual symptoms' },
    { number: '04', title: 'Results', description: 'Get the most likely issue with confidence levels' },
    { number: '05', title: 'Action', description: 'Receive recommended treatments' }
  ];

  return (
    <>
      <SEO 
        title="AI Crop Health Detection - Smart Plant Disease Diagnosis"
        description="Protect your crops with AI-powered image-based diagnosis. Identify diseases, pests, and nutrient issues instantly. Works on mobile and desktop."
        keywords="crop disease detection, plant disease identification, AI agriculture, smart farming, crop health, plant diagnosis, agricultural AI, farm technology"
        url="/crop-disease-detection"
        product={{
          name: "AI Crop Health Detection",
          description: "AI-powered platform that analyzes plant images to identify diseases, pests, and nutrient issues for smarter farming.",
          operatingSystem: "Web",
          features: "Image-based Detection, Treatment Recommendations, Multi-Crop Support, Mobile-friendly"
        }}
      />

      {/* Hero Section - Unique Gradient Design */}
      <section className="crop-hero">
        <div className="crop-hero-bg">
          <div className="crop-hero-gradient"></div>
          <div className="crop-hero-pattern"></div>
          <div className="crop-hero-glow crop-hero-glow-1"></div>
          <div className="crop-hero-glow crop-hero-glow-2"></div>
        </div>
        <div className="container">
          <div className="crop-hero-content">
            <div className="crop-hero-badge">
              <span className="badge-dot"></span>
              AI-Powered Agriculture
            </div>
            <h1>
              <span className="hero-line-2">Crop Health</span>
              <span className="hero-line-3">Detection</span>
            </h1>
            <p className="crop-hero-subtitle">
              Protect your crops with intelligent image-based diagnosis.
            </p>
            <div className="crop-hero-actions">
              <button 
                className="crop-btn crop-btn-primary" 
                onClick={() => window.open('https://plant.fractallogy.com/', '_blank')}
              >
                <span>Try Demo Free</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
              <button 
                className="crop-btn crop-btn-secondary" 
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="crop-intro">
        <div className="container">
          <div className="crop-intro-grid">
            <div className="crop-intro-visual">
              <div className="crop-phone-mockup">
                <div className="phone-screen">
                  <div className="phone-header">
                    <span className="phone-time">9:41</span>
                    <div className="phone-notch"></div>
                    <div className="phone-icons">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <div className="phone-content">
                    {/* Step 1: Upload */}
                    <div className={`phone-step phone-step-upload ${phoneStep === 1 ? 'active' : ''}`}>
                      <div className="upload-area">
                        <div className="upload-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                        </div>
                        <span className="upload-text">Drop image here</span>
                        <div className="upload-progress">
                          <div className="upload-progress-fill"></div>
                        </div>
                        <span className="upload-status">Uploading...</span>
                      </div>
                    </div>

                    {/* Step 2: Analyze */}
                    <div className={`phone-step phone-step-analyze ${phoneStep === 2 ? 'active' : ''}`}>
                      <div className="scan-animation">
                        <div className="scan-circle"></div>
                        <div className="scan-line"></div>
                        <span className="leaf-icon">🌿</span>
                      </div>
                      <div className="scan-result">
                        <span className="result-label">Analyzing...</span>
                        <div className="result-bar">
                          <div className="result-fill"></div>
                        </div>
                      </div>
                    </div>

                    {/* Step 3: Report */}
                    <div className={`phone-step phone-step-report ${phoneStep === 3 ? 'active' : ''}`}>
                      <div className="report-card">
                        <div className="report-header">
                          <span className="report-badge success">Detected</span>
                        </div>
                        <div className="report-crop">
                          <span className="report-emoji">🍅</span>
                          <div className="report-info">
                            <span className="report-name">Tomato</span>
                            <span className="report-disease">Late Blight</span>
                          </div>
                        </div>
                        <div className="report-confidence">
                          <div className="confidence-header">
                            <span>Confidence</span>
                            <span className="confidence-value">94%</span>
                          </div>
                          <div className="confidence-bar">
                            <div className="confidence-fill" style={{ width: '94%' }}></div>
                          </div>
                        </div>
                        <div className="report-action">
                          <span>View Treatment</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Step Indicators */}
                    <div className="phone-step-dots">
                      <span className={`dot ${phoneStep === 1 ? 'active' : ''}`}></span>
                      <span className={`dot ${phoneStep === 2 ? 'active' : ''}`}></span>
                      <span className={`dot ${phoneStep === 3 ? 'active' : ''}`}></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="crop-intro-text">
              <h2>Smart Diagnosis for Modern Farming</h2>
              <p>
                Our crop health platform uses advanced artificial intelligence to analyze plant images 
                and identify diseases, pests, and nutrient issues. With a simple photo, growers can 
                receive fast and reliable insights that help them take timely action and reduce losses.
              </p>
              <p>
                The system is designed for farmers, agronomists, agri-businesses, and home growers 
                who need quick, accurate plant diagnostics without complex tools or lab testing.
              </p>
              <div className="crop-intro-stats">
                <div className="stat-item">
                  <span className="stat-number">98%</span>
                  <span className="stat-label">Accuracy</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">&lt;3s</span>
                  <span className="stat-label">Detection Time</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Disease Classes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Section */}
      <section 
        className="crop-why"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(226, 232, 240, 0.92) 100%), url(${process.env.PUBLIC_URL}/images/bg_abstraction.jpg)`
        }}
      >
        <div className="container">
          <div className="crop-why-content">
            <div className="crop-why-text">
              <h2>Why AI-Powered Detection?</h2>
              <p>
                Traditional crop inspection relies on visual observation and expert advice, 
                which can be slow, costly, or unavailable in many regions. Modern AI technology 
                makes it possible to identify plant problems directly from photos taken in the 
                field or garden.
              </p>
              <p>
                Using deep learning models trained on large datasets of real crop images, 
                the system can recognize disease patterns, pest damage, and nutrient deficiencies 
                with high accuracy. This allows users to receive instant feedback instead of 
                waiting for laboratory results or specialist visits.
              </p>
            </div>
            <div className="crop-why-visual">
              <div className="ai-brain">
                <div className="brain-ring brain-ring-1"></div>
                <div className="brain-ring brain-ring-2"></div>
                <div className="brain-ring brain-ring-3"></div>
              <div className="brain-core">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" fill="white" opacity="0.9"/>
                  <path d="M2 17l10 5 10-5" stroke="white" strokeWidth="1.5" fill="none"/>
                  <path d="M2 12l10 5 10-5" stroke="white" strokeWidth="1.5" fill="none"/>
                </svg>
              </div>
                <div className="brain-node brain-node-1"></div>
                <div className="brain-node brain-node-2"></div>
                <div className="brain-node brain-node-3"></div>
                <div className="brain-node brain-node-4"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NDVI Satellite Analysis Section */}
      <section className="crop-ndvi">
        <div className="container">
          <div className="ndvi-grid">
            <div className="ndvi-content">
              <span className="section-tag">Satellite Analysis</span>
              <h2>NDVI Vegetation Index Monitoring</h2>
              <p>
                Go beyond leaf-level detection with satellite-powered field analysis. 
                Select your land on the map, and our system fetches real-time satellite 
                imagery to generate comprehensive NDVI reports.
              </p>
              <p>
                Monitor vegetation health, detect stress patterns, and identify nutrient 
                deficiencies across your entire farm — all from space.
              </p>
              <ul className="ndvi-features">
                <li><span className="ndvi-dot green"></span> Vegetation Health & Density</li>
                <li><span className="ndvi-dot yellow"></span> Crop Stress Detection</li>
                <li><span className="ndvi-dot orange"></span> Soil & Nutrient Deficiency</li>
                <li><span className="ndvi-dot blue"></span> Growth Vigor Analysis</li>
              </ul>
            </div>
            <div className="ndvi-visual">
              <div className="ndvi-mockup">
                <div className="ndvi-screen">
                  <div className="ndvi-header">
                    <span className="ndvi-title">NDVI Analysis</span>
                    <div className="ndvi-controls">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>

                  {/* Step 1: Select Land */}
                  <div className={`ndvi-step ndvi-step-map ${ndviStep === 1 ? 'active' : ''}`}>
                    <div className="map-container">
                      <img 
                        src={`${process.env.PUBLIC_URL}/images/map.jpg`} 
                        alt="Select Field Area" 
                        className="map-image"
                      />
                      <div className="map-marker">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#00c853"/>
                          <circle cx="12" cy="9" r="2.5" fill="white"/>
                        </svg>
                      </div>
                      <div className="selection-box"></div>
                    </div>
                    <div className="map-label">Select your field area</div>
                  </div>

                  {/* Step 2: Fetch Satellite */}
                  <div className={`ndvi-step ndvi-step-satellite ${ndviStep === 2 ? 'active' : ''}`}>
                    <div className="satellite-animation">
                      <div className="globe-container">
                        <img 
                          src={`${process.env.PUBLIC_URL}/images/globe.png`} 
                          alt="Earth Satellite View" 
                          className="globe-image"
                        />
                        <div className="globe-scan-ring"></div>
                        <div className="satellite-orbit">
                          <img 
                            src={`${process.env.PUBLIC_URL}/images/satellite.png`} 
                            alt="Satellite" 
                            className="satellite-img"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="satellite-label">Fetching satellite imagery...</div>
                  </div>

                  {/* Step 3: Processing */}
                  <div className={`ndvi-step ndvi-step-process ${ndviStep === 3 ? 'active' : ''}`}>
                    <div className="process-animation">
                      <div className="process-visual">
                        <div className="data-layers">
                          <div className="data-layer data-layer-1"></div>
                          <div className="data-layer data-layer-2"></div>
                          <div className="data-layer data-layer-3"></div>
                        </div>
                        <div className="process-loader">
                          <div className="loader-ring"></div>
                          <span>AI</span>
                        </div>
                      </div>
                    </div>
                    <div className="process-label">Processing Landsat data...</div>
                  </div>

                  {/* Step 4: NDVI Report */}
                  <div className={`ndvi-step ndvi-step-report ${ndviStep === 4 ? 'active' : ''}`}>
                    <div className="ndvi-report">
                      <div className="ndvi-map-preview">
                        <img 
                          src={`${process.env.PUBLIC_URL}/images/ndvi.jpg`} 
                          alt="NDVI Analysis Map" 
                          className="ndvi-map-image"
                        />
                      </div>
                      <div className="ndvi-metrics">
                        <div className="metric-row">
                          <span className="metric-label">Health</span>
                          <div className="metric-bar"><div className="metric-fill health" style={{width: '85%'}}></div></div>
                          <span className="metric-value good">Good</span>
                        </div>
                        <div className="metric-row">
                          <span className="metric-label">Density</span>
                          <div className="metric-bar"><div className="metric-fill density" style={{width: '72%'}}></div></div>
                          <span className="metric-value moderate">72%</span>
                        </div>
                        <div className="metric-row">
                          <span className="metric-label">Stress</span>
                          <div className="metric-bar"><div className="metric-fill stress" style={{width: '23%'}}></div></div>
                          <span className="metric-value low">Low</span>
                        </div>
                        <div className="metric-row">
                          <span className="metric-label">Vigor</span>
                          <div className="metric-bar"><div className="metric-fill vigor" style={{width: '78%'}}></div></div>
                          <span className="metric-value good">High</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step Indicators */}
                  <div className="ndvi-step-dots">
                    <span className={`dot ${ndviStep === 1 ? 'active' : ''}`}></span>
                    <span className={`dot ${ndviStep === 2 ? 'active' : ''}`}></span>
                    <span className={`dot ${ndviStep === 3 ? 'active' : ''}`}></span>
                    <span className={`dot ${ndviStep === 4 ? 'active' : ''}`}></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="crop-steps">
        <div className="container">
          <div className="section-header-center">
            <span className="section-tag">Process</span>
            <h2>How It Works</h2>
          </div>
          <div className="steps-timeline">
            {steps.map((step, index) => (
              <div key={index} className="step-item">
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < steps.length - 1 && <div className="step-connector"></div>}
              </div>
            ))}
          </div>
          <p className="steps-note">
            The model is trained using thousands to millions of labeled crop images, 
            allowing it to learn patterns similar to how agricultural experts study plant diseases.
          </p>
        </div>
      </section>

      {/* B2B Enterprise Section */}
      <section className="crop-b2b">
        <div className="container">
          <div className="b2b-content">
            <div className="b2b-badge">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>Enterprise Solution</span>
            </div>
            <h2>Built for Business</h2>
            <p className="b2b-lead">
              This is a <strong>B2B product</strong> designed for agribusinesses, agricultural enterprises, 
              and technology partners who want to integrate AI-powered crop detection into their platforms.
            </p>
            <div className="b2b-features">
              <div className="b2b-feature">
                <h4>Enterprise Deployment</h4>
                <p>On-premise or cloud hosting with dedicated infrastructure for your organization</p>
              </div>
              <div className="b2b-feature">
                <h4>API Integration</h4>
                <p>RESTful APIs to embed crop detection directly into your existing applications</p>
              </div>
              <div className="b2b-feature">
                <h4>White-Label Solution</h4>
                <p>Fully customizable interface branded to match your company identity</p>
              </div>
              <div className="b2b-feature">
                <h4>Custom Model Training</h4>
                <p>Train the AI on your specific crops, regions, and disease patterns</p>
              </div>
            </div>
            <div className="b2b-cta">
              <button 
                className="crop-btn crop-btn-primary"
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              >
                Request Enterprise Demo
              </button>
              <p className="b2b-note">Contact our team for pricing and integration details</p>
            </div>
          </div>
        </div>
      </section>

      <ContactCropDisease />
      <Footer />
    </>
  );
};

export default CropDiseaseDetection;
